import React, { createContext, useState, useEffect, useContext } from 'react';

import {
  registerUser,
  loginUser,
  logoutUser,
  updateUserProfile,
  getCurrentUser,
  sendOtp,
  verifyOtp,
  refreshToken
} from '../services/authService';
import PageLoader from '../components/ui/PageLoader';

// JWT token decoder function
const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

// Check if token is expired or about to expire (within 5 minutes)
const isTokenExpired = (token) => {
  if (!token) return true;
  
  const decodedToken = parseJwt(token);
  if (!decodedToken) return true;
  
  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = decodedToken.exp * 1000;
  const currentTime = Date.now();
  const timeBuffer = 5 * 60 * 1000; // 5 minutes in milliseconds
  
  return currentTime + timeBuffer >= expirationTime;
};

// Create the context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check and refresh token if needed
  const checkAndRefreshToken = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token && isTokenExpired(token)) {
        console.log('Token is expired or about to expire, refreshing...');
        await refreshToken();
      }
    } catch (err) {
      console.error('Error refreshing token:', err);
      // If refresh fails, we'll let the response interceptor handle it
    }
  };

  // Check for existing session on initial load
  useEffect(() => {
    const initializeAuth = async () => {
      // Regular auth check for all users
      setLoading(true);
      try {
        await checkAndRefreshToken();
        
        const localUser = JSON.parse(localStorage.getItem('user'));
        if (localUser) {
          setCurrentUser(localUser);
          setUserRole(localUser.role || '');
        } else {
          const user = await getCurrentUser();
          if (user) {
            setCurrentUser(user);
            setUserRole(user.role || '');
          }
        }
      } catch (err) {
        console.error('Auth initialization failed:', err);
      } finally {
        setLoading(false);
      }
    };
    
    initializeAuth();
    
    // Set up periodic token refresh check
    const tokenRefreshInterval = setInterval(checkAndRefreshToken, 15 * 60 * 1000); // Check every 15 minutes
    
    return () => {
      clearInterval(tokenRefreshInterval);
    };
  }, []);

  // Register function
  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      const user = await registerUser(userData);
      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Send verification code
  const sendVerificationCodeEmail = async (email) => {
    try {
      setLoading(true);
      setError(null);
      
      const code = await sendOtp(email);
      return code;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Verify email code
  const verifyEmailCode = async (email, code) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await verifyOtp(email, code);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const user = await loginUser(email, password);
      
      if (user) {
        setCurrentUser(user);
        setUserRole(user.role || '');
      }
      
      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      // Add small delay for smooth transition
      setTimeout(() => {
        setLoading(false);
      }, 100);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setLoading(true);
      await logoutUser();
      setCurrentUser(null);
      setUserRole('');
      
      return true;
    } catch (err) {
      console.error('Logout failed:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update user profile
  const updateProfile = async (userData) => {
    if (!currentUser) {
      throw new Error('No user is logged in');
    }
    setLoading(true);
    setError(null);
    try {
      // Handle profile picture separately if it's a file
      if (userData instanceof FormData) {
        // If FormData is passed directly, use updateUserProfile
        const response = await updateUserProfile(userData);
        
        if (response) {
          // Update the current user with the response
          const updatedUser = { ...currentUser, ...response };
          setCurrentUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
          return updatedUser;
        }
      } else if (userData.profile_picture && userData.profile_picture instanceof File) {
        // If profile_picture is a File, create FormData
        const formData = new FormData();
        formData.append('profile_picture', userData.profile_picture);
        
        // Add other fields if present
        Object.entries(userData).forEach(([key, value]) => {
          if (key !== 'profile_picture' && value !== undefined && value !== null) {
            formData.append(key, value);
          }
        });
        
        // Make API call
        const response = await updateUserProfile(formData);
        
        if (response) {
          // Update the current user with the response
          const updatedUser = { ...currentUser, ...response };
          setCurrentUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
          return updatedUser;
        }
      } else {
        // For regular profile updates (no files)
        const response = await updateUserProfile(userData);
        
        if (response) {
          // Update the current user with the response
          const updatedUser = { ...currentUser, ...response };
          setCurrentUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
          
          if (userData.role && userData.role !== userRole) {
            setUserRole(userData.role);
          }
          
          return updatedUser;
        }
      }
      
      return currentUser;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    userRole,
    loading,
    error,
    register,
    login,
    logout,
    updateProfile,
    sendVerificationCodeEmail,
    verifyEmailCode,
    isAdmin: userRole === 'admin',
    isSeller: userRole === 'seller',
    isBuyer: userRole === 'buyer'
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <PageLoader isLoading={true} />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
